/**
 * Financial metric definitions with explanations and formulas
 * Used for tooltip content in financial metrics display
 */

export interface MetricDefinition {
  key: string;
  label: string;
  mobileLabel: string;
  explanation: string;
  formula: string;
  example?: string;
}

export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
  // Cash & Debt
  cash: {
    key: 'cash',
    label: 'Cash',
    mobileLabel: 'Cash',
    explanation: 'Total cash and cash equivalents available from the last full fiscal year. The most liquid asset on the balance sheet.',
    formula: 'Cash = Cash + Cash Equivalents',
  },
  debt: {
    key: 'debt',
    label: 'Debt',
    mobileLabel: 'Debt',
    explanation: 'Total long-term debt owed to lenders, including bank loans, bonds, and capitalized leases. Higher debt means higher interest payments.',
    formula: 'Debt = Long-term Debt + Capitalized Lease',
  },

  // Company Size
  marketCapitalization: {
    key: 'marketCapitalization',
    label: 'Market Capitalization',
    mobileLabel: 'Mkt Cap',
    explanation: 'The total value of all outstanding shares. Represents what investors think the entire company is worth.',
    formula: 'Market Cap = Stock Price × Shares Outstanding',
  },
  enterpriseValue: {
    key: 'enterpriseValue',
    label: 'Enterprise Value',
    mobileLabel: 'Ent Value',
    explanation: 'The total value of a company including its debt minus cash. A more complete picture than market cap for acquisition valuation.',
    formula: 'Enterprise Value = Market Cap + Debt - Cash',
  },
  debtToEquity: {
    key: 'debtToEquity',
    label: 'Debt-to-Equity',
    mobileLabel: 'D/E',
    explanation: 'How much debt the company uses relative to shareholder equity. Higher ratio means more leverage — greater risk but also greater potential returns.',
    formula: 'D/E = Total Debt / Shareholders\' Equity',
  },

  // Profitability & Growth
  annualRevenue: {
    key: 'annualRevenue',
    label: 'Annual Revenue (TTM)',
    mobileLabel: 'Revenue',
    explanation: 'Total money earned from selling products or services over the last 12 months (trailing twelve months). This is the "top line" before expenses.',
    formula: 'Revenue (TTM) = Sum of revenue from last 4 quarters',
  },
  quarterlyRevenueGrowth: {
    key: 'quarterlyRevenueGrowth',
    label: 'Revenue Growth',
    mobileLabel: 'YoY Rev Growth',
    explanation: 'Year-over-year revenue growth comparing the most recent quarter to the same quarter one year ago. Positive growth means the company is selling more.',
    formula: 'YoY Growth = (Latest Quarter Rev - Same Quarter Last Year) / Same Quarter Last Year × 100',
  },
  leveredFreeCashFlow: {
    key: 'leveredFreeCashFlow',
    label: 'Levered FCF (TTM)',
    mobileLabel: 'Free Cash Flow',
    explanation: 'Cash generated after paying all operating expenses, capital expenditures, and interest. Available for dividends, buybacks, or debt reduction.',
    formula: 'Levered FCF = Operating Cash Flow - CapEx - Interest Payments',
  },
  netIncome: {
    key: 'netIncome',
    label: 'Net Income',
    mobileLabel: 'Net Income',
    explanation: 'The company\'s "bottom line" profit after all expenses, taxes, interest, and preferred dividends over the trailing twelve months.',
    formula: 'Net Income = Revenue - All Expenses - Taxes - Interest - Preferred Dividends',
  },
  dilutedEps: {
    key: 'dilutedEps',
    label: 'Diluted EPS',
    mobileLabel: 'DILUTED EPS',
    explanation: 'Profit per share assuming all stock options and convertible securities are exercised. More conservative than basic EPS.',
    formula: 'Diluted EPS = Net Income / Weighted Average Diluted Shares Outstanding',
  },

  // Dividends
  fiveYearDividendYield: {
    key: 'fiveYearDividendYield',
    label: '5 Year Dividend Yield',
    mobileLabel: '5Y Div Yield',
    explanation: 'Average annual dividend as a percentage of stock price over the past 5 years. Higher yield means more income returned to investors.',
    formula: '5Y Avg Dividend Yield = Average(Annual Dividend / Stock Price) over 5 years',
  },

  // Valuation Ratios
  priceToSales: {
    key: 'priceToSales',
    label: 'PS (TTM)',
    mobileLabel: 'P/S',
    explanation: 'How many dollars investors pay for each dollar of revenue. Lower ratios may indicate cheaper valuation.',
    formula: 'P/S = Market Cap / Annual Revenue (TTM)',
  },
  industryPriceToSales: {
    key: 'industryPriceToSales',
    label: 'Industry PS (TTM)',
    mobileLabel: 'Ind P/S',
    explanation: 'Average P/S ratio for companies in the same industry. Compare to the company\'s P/S to gauge relative valuation.',
    formula: 'Industry P/S = Average P/S of all companies in the sector',
  },
  trailingPE: {
    key: 'trailingPE',
    label: 'Trailing PE',
    mobileLabel: 'P/E',
    explanation: 'How many dollars investors pay for each dollar of earnings. One of the most common valuation metrics — lower P/E may mean cheaper stock.',
    formula: 'P/E = Stock Price / Earnings Per Share (TTM)',
  },
  industryTrailingPE: {
    key: 'industryTrailingPE',
    label: 'Industry Trailing PE',
    mobileLabel: 'Ind P/E',
    explanation: 'Average P/E ratio for companies in the same industry. Compare to the company\'s P/E to see if it trades at a premium or discount.',
    formula: 'Industry P/E = Average P/E of all companies in the sector',
  },

  // Dynamic group metrics
  operatingMargin: {
    key: 'operatingMargin',
    label: 'Operating Margin',
    mobileLabel: 'Op Margin',
    explanation: 'Percentage of revenue remaining after operating expenses. Measures core business efficiency before interest and taxes.',
    formula: 'Operating Margin = Operating Income / Revenue',
  },
  profitMargin: {
    key: 'profitMargin',
    label: 'Net Margin',
    mobileLabel: 'Net Margin',
    explanation: 'Percentage of revenue that becomes net profit after all expenses. Higher margin means the company keeps more of each dollar earned.',
    formula: 'Profit Margin = Net Income / Revenue',
  },
  grossProfit: {
    key: 'grossProfit',
    label: 'Gross Profit',
    mobileLabel: 'Gross Profit',
    explanation: 'Revenue minus the direct cost of producing goods or services (COGS) over the trailing twelve months.',
    formula: 'Gross Profit = Revenue - Cost of Goods Sold',
  },
  grossMargin: {
    key: 'grossMargin',
    label: 'Gross Margin',
    mobileLabel: 'Gross Margin',
    explanation: 'Percentage of revenue remaining after subtracting production costs. Shows how efficiently the company produces its products.',
    formula: 'Gross Margin = (Revenue - COGS) / Revenue',
    example: 'If revenue is $10B and COGS is $4B, gross margin = 60%',
  },
  operatingCashFlow: {
    key: 'operatingCashFlow',
    label: 'Operating Cash Flow (TTM)',
    mobileLabel: 'Op Cash Flow',
    explanation: 'Cash generated from core business operations. Unlike net income, excludes non-cash items and includes working capital changes.',
    formula: 'Operating Cash Flow = Net Income + Non-Cash Charges + Changes in Working Capital',
  },
  revenuePerShare: {
    key: 'revenuePerShare',
    label: 'Revenue Per Share (TTM)',
    mobileLabel: 'Rev per Share',
    explanation: 'Total revenue divided by shares outstanding. Shows how much revenue each share represents.',
    formula: 'Revenue Per Share = Total Revenue (TTM) / Shares Outstanding',
  },
  bookValuePerShare: {
    key: 'bookValuePerShare',
    label: 'Book Value Per Share',
    mobileLabel: 'Book per Share',
    explanation: 'Net asset value per share — what each share would theoretically be worth if the company liquidated all assets.',
    formula: 'Book Value/Share = (Total Assets - Total Liabilities) / Shares Outstanding',
  },
  forwardPE: {
    key: 'forwardPE',
    label: 'Forward P/E',
    mobileLabel: 'Fwd P/E',
    explanation: 'Stock price divided by estimated future earnings. Uses analyst forecasts for the next 12 months instead of past earnings.',
    formula: 'Forward P/E = Stock Price / Estimated Future EPS',
  },
  priceToBook: {
    key: 'priceToBook',
    label: 'Price to Book',
    mobileLabel: 'P/B',
    explanation: 'Stock price relative to book value per share. Below 1.0 means it trades below net asset value; above 1.0 reflects a market premium.',
    formula: 'P/B = Stock Price / Book Value Per Share',
  },
  evToRevenue: {
    key: 'evToRevenue',
    label: 'EV/Revenue',
    mobileLabel: 'EV/Rev',
    explanation: 'Enterprise value divided by revenue. Accounts for debt, making it useful for comparing companies with different capital structures.',
    formula: 'EV/Revenue = Enterprise Value / Annual Revenue',
  },
  pegRatio: {
    key: 'pegRatio',
    label: 'PEG Ratio',
    mobileLabel: 'PEG',
    explanation: 'P/E ratio divided by earnings growth rate. A PEG of 1.0 suggests fair valuation; below 1.0 may be undervalued, above 1.0 overvalued.',
    formula: 'PEG = (P/E Ratio) / Earnings Growth Rate (%)',
  },
  earningsGrowthYoY: {
    key: 'earningsGrowthYoY',
    label: 'Earnings Growth (YoY)',
    mobileLabel: 'Earn Growth',
    explanation: 'Year-over-year growth in quarterly earnings compared to the same quarter one year ago.',
    formula: 'Earnings Growth = (Current Quarter EPS - Same Quarter Last Year EPS) / Same Quarter Last Year EPS × 100',
  },
  institutionalOwnership: {
    key: 'institutionalOwnership',
    label: 'Institutional Ownership',
    mobileLabel: '% Inst. Own',
    explanation: 'Percentage of shares owned by mutual funds, pension funds, and hedge funds. High ownership can signal professional confidence.',
    formula: 'Institutional Ownership = Shares Held by Institutions / Total Shares Outstanding × 100',
  },
  insiderOwnership: {
    key: 'insiderOwnership',
    label: 'Insider Ownership',
    mobileLabel: '% Insider Own',
    explanation: 'Percentage of shares owned by company executives and directors. Higher ownership aligns management interests with shareholders.',
    formula: 'Insider Ownership = Shares Held by Insiders / Total Shares Outstanding × 100',
  },
  shortRatio: {
    key: 'shortRatio',
    label: 'Short Ratio',
    mobileLabel: 'Short Ratio',
    explanation: 'Days it would take short sellers to cover their positions based on average daily volume. Higher ratio can amplify price moves.',
    formula: 'Short Ratio = Shares Sold Short / Average Daily Volume',
  },

  // Combined metrics (for dual-value cards)
  trailingPE_combined: {
    key: 'trailingPE_combined',
    label: 'P/E (Ind. Avg.)',
    mobileLabel: 'P/E (Ind.)',
    explanation: 'P/E ratio with the industry average in parentheses. Helps determine if the stock is valued higher or lower than peers.',
    formula: 'P/E = Stock Price / Earnings Per Share (TTM)',
  },
  priceToSales_combined: {
    key: 'priceToSales_combined',
    label: 'P/S (Ind. Avg.)',
    mobileLabel: 'P/S (Ind.)',
    explanation: 'P/S ratio with the industry average in parentheses. Helps determine if the stock is expensive or cheap relative to peers.',
    formula: 'P/S = Market Cap / Annual Revenue (TTM)',
  },
};

/**
 * Get metric definition by key
 */
export const getMetricDefinition = (key: string): MetricDefinition | undefined => {
  return METRIC_DEFINITIONS[key];
};

/**
 * Get all metric definitions as array
 */
export const getAllMetricDefinitions = (): MetricDefinition[] => {
  return Object.values(METRIC_DEFINITIONS);
};

/**
 * Metric groups for organized display
 */
export const METRIC_GROUPS = {
  companySize: {
    title: 'Company Size',
    metrics: ['marketCapitalization', 'enterpriseValue', 'debtToEquity', 'cash', 'debt']
  },
  profitabilityGrowth: {
    title: 'Profitability & Growth',
    metrics: ['annualRevenue', 'quarterlyRevenueGrowth', 'leveredFreeCashFlow', 'netIncome', 'dilutedEps', 'fiveYearDividendYield']
  },
  valuationRatios: {
    title: 'Valuation Ratios',
    metrics: ['priceToSales', 'industryPriceToSales', 'trailingPE', 'industryTrailingPE']
  }
} as const;

/**
 * Metric tiers for progressive unlock (3+3+3+3 = 12 metrics)
 */
export const METRIC_TIERS = {
  tier0: {
    title: 'Game Start',
    metrics: ['annualRevenue', 'quarterlyRevenueGrowth', 'dilutedEps']
  },
  tier1: {
    title: 'After Guess 1',
    metrics: ['netIncome', 'leveredFreeCashFlow', 'fiveYearDividendYield']
  },
  tier2: {
    title: 'After Guess 2',
    metrics: ['cash', 'debt', 'debtToEquity']
  },
  tier3: {
    title: 'After Guess 3',
    metrics: ['trailingPE_combined', 'priceToSales_combined', 'marketCapitalization']
  }
} as const;

/**
 * Display labels for each metric tier (used in row-based layout)
 */
export const TIER_DISPLAY_CONFIG: Record<string, string> = {
  tier0: 'Basics',
  tier1: 'Profitability',
  tier2: 'Balance Sheet',
  tier3: 'Valuation',
};

/**
 * Combined metrics mapping - maps combined metric keys to their primary/secondary value keys
 */
export const COMBINED_METRICS: Record<string, { primary: string; secondary: string }> = {
  trailingPE_combined: { primary: 'trailingPE', secondary: 'industryTrailingPE' },
  priceToSales_combined: { primary: 'priceToSales', secondary: 'industryPriceToSales' }
};
